import type {ReactElement} from 'react';
import {useLayoutEffect} from 'react';
import React, {useRef} from 'react';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {AnimatePresence, motion} from 'framer-motion';
import useOutsideClick from '@Hooks/useOutsideClick';
import {isMobile} from 'react-device-detect';
import styles from './contextual-menu.module.scss';
import {useAppDispatch} from '@/hooks';
import {updateScrollData} from '@Components/contextual-menu/contextual-menu.reducer';

interface ContextualMenuProps extends ContextualMenuOutsideClickProperties, RetainContextualMenuProps {
  id: string;
  className?: string;
  contentClassName?: string;
  children: (ReactElement | null)[] | ReactElement | null;
  header?: ReactElement | null;
  footerContent?: ReactElement | null;
  includeLeftRightPadding?: boolean;
  hasMobileModalPadding?: boolean;
  hasMobileMorphingProperties?: boolean;
}

interface ContextualMenuOutsideClickProperties {
  idsToFireOutsideClickOn?: string[];
  onOutsideClick?(): void;
  fireOnOutsideClickOnCanvasClick?: boolean;
}

interface RetainContextualMenuProps {
  contentKey?: string;
  doRetainScroll?: boolean;
}

export function ContextualMenu({
  className = '',
  contentClassName = '',
  idsToFireOutsideClickOn = ['poster-whiteboard-container'],
  onOutsideClick = (): void => {},
  includeLeftRightPadding = true,
  fireOnOutsideClickOnCanvasClick = true,
  hasMobileModalPadding = false,
  hasMobileMorphingProperties = false,
  contentKey = '',
  doRetainScroll = true,
  ...props
}: ContextualMenuProps): ReactElement {
  const containerRef = useRef<HTMLUListElement>(null);
  const contextualMenuRef = useRef<HTMLDivElement>(null);
  const {topShadow} = useTopBottomScrollShadow(containerRef);
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (!doRetainScroll || contentKey === '') {
      return;
    }
    const position = window.PMW.redux.store.getState().contextualMenu.scrollData[contentKey] ?? 0;
    if (containerRef.current) {
      containerRef.current.scrollTop = position;
    }
  }, [contentKey]);

  const onScroll = (): void => {
    if (containerRef.current && contentKey !== '') {
      dispatch(
        updateScrollData({
          key: contentKey,
          scrollValue: containerRef.current.scrollTop,
        })
      );
    }
  };

  const getContextualMenuContent = (): ReactElement | (ReactElement | null)[] | null => {
    return props.children;
  };

  const getContainerHeightClasses = (): string => {
    if (!props.header && !props.footerContent) {
      return styles.contextualMenuWithoutHeader;
    }
    if (!props.footerContent) {
      return styles.contextualMenuWithHeader;
    }
    if (!props.header) {
      return styles.contextualMenuWithFooter;
    }
    return styles.contextualMenuWithHeaderAndFooter;
  };

  const onOutsideClickCallback = (e: MouseEvent): void => {
    const el = e.target as HTMLDivElement;
    if (!el) {
      return;
    }

    if (fireOnOutsideClickOnCanvasClick && el.nodeName === 'CANVAS') {
      onOutsideClick();
      return;
    }

    for (let i = 0; i < idsToFireOutsideClickOn?.length; i += 1) {
      if (idsToFireOutsideClickOn[i] === el.id) {
        onOutsideClick();
        return;
      }
    }
  };

  useOutsideClick(contextualMenuRef, onOutsideClickCallback);

  return (
    <div key={props.id} ref={contextualMenuRef} id={props.id} className={`${className} ${styles.container} ${isMobile ? styles.mobileContextualMenu : ''}`}>
      {props.header}
      <AnimatePresence>
        <motion.div className={getContainerHeightClasses()} animate={{opacity: 1}} initial={{opacity: 0}} transition={{duration: 0.2}} exit={{opacity: 0}}>
          <ul
            ref={containerRef}
            onScroll={onScroll}
            className={`${contentClassName} ${styles.contextualMenuContainer} ${
              hasMobileModalPadding ? 'spacing-p-t-1 spacing-p-b-4 spacing-p-r-4 spacing-p-l-4' : ''
            } flex-v-row ${includeLeftRightPadding && !hasMobileModalPadding ? 'spacing-p-r-5 spacing-p-l-5' : ''} ${hasMobileMorphingProperties ? styles.mobileMorph : ''} ${
              topShadow ? styles.topShadow : ''
            }`}
          >
            {getContextualMenuContent()}
          </ul>
        </motion.div>
      </AnimatePresence>
      {props.footerContent ? <div className={`${styles.footer} flex-center`}>{props.footerContent}</div> : null}
    </div>
  );
}
